/**
 * LotteryKPIDisplay - Reusable KPI v2 display component for lottery pages
 * 
 * Displays KPI v2 (Performance vs Random) for individual lottery pages.
 * Shows either:
 * - Case A: KPI values when data is available
 * - Case B: "Insufficient data" placeholder when not enough predictions
 * 
 * DO NOT use GEAI/KPI v3 on lottery pages - this is KPI v2 only.
 */

import React from 'react';
import { TrendingUp, Award, BarChart3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';

// Format performance index for display
const formatPI = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(2)}×`;
};

// Check if model data indicates insufficient data
const hasInsufficientData = (modelData) => {
  if (!modelData) return true;
  if (modelData.status === 'insufficient_data') return true;
  // API returns 'predictions', not 'total_predictions'
  const predCount = modelData.predictions || modelData.total_predictions || 0;
  if (predCount < 3) return true;
  if (modelData.performance_index === null || modelData.performance_index === undefined) return true;
  return false;
};

// Single model KPI card
const ModelKPICard = ({ modelData, modelName, modelLabel, labelColor, icon }) => {
  const isInsufficient = hasInsufficientData(modelData);
  
  if (isInsufficient) {
    return (
      <div className="p-2 bg-gray-800/50 rounded-lg border border-gray-700/50">
        <div className="flex items-center gap-1 mb-1">
          <span className={`text-[10px] font-semibold ${labelColor}`}>{icon} {modelLabel}</span>
        </div>
        <div className="text-xs text-gray-400">
          Not enough historical data yet
        </div>
        <div className="text-[9px] text-gray-500 mt-1">
          KPI will appear once sufficient draws are evaluated.
        </div>
      </div>
    );
  }
  
  const isOverperforming = modelData.performance_index > 1.00;
  
  return (
    <div className="p-2 bg-gray-800/50 rounded-lg border border-gray-700/50">
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] font-semibold ${labelColor}`}>{icon} {modelLabel}</span>
        {isOverperforming && (
          <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
            Overperforming
          </span>
        )}
      </div>
      <div className="text-sm font-bold text-white mb-1">
        {formatPI(modelData.performance_index)}
        <span className="text-[9px] font-normal text-gray-500 ml-1">vs random</span>
      </div>
      <div className="grid grid-cols-3 gap-1 text-[9px]">
        <div>
          <div className="text-gray-500">Avg hits</div>
          <div className="text-white">{(modelData.avg_hits ?? modelData.avg_total_hits)?.toFixed(2) || '—'}</div>
        </div>
        <div>
          <div className="text-gray-500">Expected</div>
          <div className="text-white">{(modelData.expected_hits ?? modelData.expected_total_hits)?.toFixed(2) || '—'}</div>
        </div>
        <div>
          <div className="text-gray-500">Predictions</div>
          <div className="text-white">{modelData.predictions || modelData.total_predictions || 0}</div>
        </div>
      </div>
      {modelData.best_result && (
        <div className="mt-1.5 pt-1.5 border-t border-gray-700/50">
          <div className="flex items-center gap-1">
            <Award className="w-2.5 h-2.5 text-amber-400" />
            <span className="text-[8px] text-gray-500">Best:</span>
            <span className="text-[9px] text-white">
              {modelData.best_result.main_hits}+{modelData.best_result.bonus_hits}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Main LotteryKPIDisplay component
 * 
 * @param {Object} props
 * @param {Object} props.kpiData - KPI v2 data from API (from /api/kpi-v2/{lottery})
 * @param {boolean} props.loading - Loading state
 * @param {string} props.className - Additional CSS classes
 */
export const LotteryKPIDisplay = ({ kpiData, loading = false, className = '' }) => {
  // Loading state
  if (loading) {
    return (
      <Card className={`bg-gray-900/80 border-gray-800 ${className}`} data-testid="kpi-display-loading">
        <CardHeader className="py-2 px-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-amber-400" />
            Prediction Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-2 px-3 space-y-2">
          <Skeleton className="w-full h-16" />
          <Skeleton className="w-full h-16" />
        </CardContent>
      </Card>
    );
  }
  
  // Check if we have any valid data
  const hasStatistical = kpiData?.models?.statistical;
  const hasLotteryx = kpiData?.models?.lotteryx;
  const hasAnyModel = hasStatistical || hasLotteryx;
  
  // Both models have insufficient data - show placeholder
  const bothInsufficient = 
    (!hasStatistical || hasInsufficientData(kpiData?.models?.statistical)) &&
    (!hasLotteryx || hasInsufficientData(kpiData?.models?.lotteryx));
  
  return (
    <Card className={`bg-gray-900/80 border-gray-800 ${className}`} data-testid="kpi-display">
      <CardHeader className="py-2 px-3">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          <TrendingUp className="w-3 h-3 text-amber-400" />
          Prediction Performance
        </CardTitle>
      </CardHeader>
      
      <CardContent className="pt-0 pb-2 px-3 space-y-2">
        {!hasAnyModel || bothInsufficient ? (
          // Case B: Insufficient data for all models
          <div className="p-3 bg-gray-800/30 rounded-lg border border-gray-700/30" data-testid="kpi-insufficient-data">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-400">Prediction Performance</span>
            </div>
            <p className="text-xs text-gray-500">
              Not enough historical data yet
            </p>
            <p className="text-[10px] text-gray-600 mt-1">
              KPI will appear once sufficient draws are evaluated.
            </p>
          </div>
        ) : (
          // Case A: Show KPI for available models
          <>
            {hasStatistical && (
              <ModelKPICard 
                modelData={kpiData.models.statistical}
                modelName="statistical"
                modelLabel="Statistical"
                labelColor="text-blue-400"
                icon="📊"
              />
            )}
            {hasLotteryx && (
              <ModelKPICard 
                modelData={kpiData.models.lotteryx}
                modelName="lotteryx"
                modelLabel="Lotterics"
                labelColor="text-amber-400"
                icon="🔥"
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LotteryKPIDisplay;
